import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, ExternalLink } from 'lucide-react';
import { api, type AiSummaryEvent, type AiSummaryRun } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

export function AiSummaryRunDetail(): JSX.Element {
  const { id = '', runId = '' } = useParams<{ id: string; runId: string }>();

  const { data } = useQuery({
    queryKey: ['ai-summary-run', runId],
    queryFn: () =>
      api.get<{ run: AiSummaryRun; events: AiSummaryEvent[] }>(
        `/admin/ai-summary/runs/${runId}`,
      ),
    refetchInterval: 5_000,
  });

  // Subdomain для построения ссылок на сделки и заметки.
  // Из run'а не приходит — берём из конфига родителя (один лишний get, кешируется).
  const { data: cfg } = useQuery({
    queryKey: ['ai-summary-config', id],
    queryFn: () =>
      api.get<{ account_subdomain: string }>(`/admin/ai-summary/configs/${id}`),
    enabled: Boolean(id),
  });

  if (!data) {
    return <div className="text-sm text-muted-foreground">Загрузка…</div>;
  }
  const { run, events } = data;
  const subdomain = cfg?.account_subdomain;

  return (
    <div className="space-y-6">
      <header className="flex items-center gap-3">
        <Button asChild variant="ghost" size="icon">
          <Link to={`/ai-summary/${id}`}>
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <div>
          <h1 className="text-2xl font-bold tracking-tight">
            Run #{run.id} <Badge variant={run.trigger === 'cron' ? 'secondary' : 'success'}>{run.trigger}</Badge>
          </h1>
          <p className="text-sm text-muted-foreground">
            Started {new Date(run.started_at).toLocaleString()}
            {run.finished_at && ` · Finished ${new Date(run.finished_at).toLocaleString()}`}
            {!run.finished_at && ' · running…'}
          </p>
        </div>
      </header>

      <section className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <KpiCard label="Всего" value={run.leads_total} />
        <KpiCard label="Готово" value={run.leads_processed} tone="success" />
        <KpiCard label="Пропущено" value={run.leads_skipped} tone="muted" />
        <KpiCard label="Ошибок" value={run.leads_failed} tone="destructive" />
      </section>

      <section className="grid grid-cols-2 gap-4 md:grid-cols-3">
        <KpiCard label="Tokens IN" value={run.llm_tokens_input} compact />
        <KpiCard label="Tokens OUT" value={run.llm_tokens_output} compact />
        <KpiCard
          label="Est. cost (Sonnet)"
          value={Math.round(
            (run.llm_tokens_input * 3 + run.llm_tokens_output * 15) / 1_000_000,
          ) /* dollars, rough */}
          suffix="$"
          compact
        />
      </section>

      {run.last_error && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base text-destructive">Run error</CardTitle>
          </CardHeader>
          <CardContent>
            <pre className="whitespace-pre-wrap text-xs">{run.last_error}</pre>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">События ({events.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {events.length === 0 ? (
            <div className="py-6 text-center text-sm text-muted-foreground">
              События появятся по мере обработки сделок.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-28">Lead</TableHead>
                  <TableHead className="w-24">Status</TableHead>
                  <TableHead>Reason / Note</TableHead>
                  <TableHead className="w-24 text-right">Tokens out</TableHead>
                  <TableHead className="w-20 text-right">ms</TableHead>
                  <TableHead className="w-40">Processed</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {events.map((e) => (
                  <TableRow key={e.id}>
                    <TableCell className="font-mono text-xs">
                      {subdomain ? (
                        <a
                          href={`https://${subdomain}/leads/detail/${e.kommo_lead_id}`}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 text-primary hover:underline"
                        >
                          {e.kommo_lead_id} <ExternalLink className="h-3 w-3" />
                        </a>
                      ) : (
                        e.kommo_lead_id
                      )}
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={e.status} />
                    </TableCell>
                    <TableCell className="max-w-md truncate text-xs text-muted-foreground">
                      {e.status === 'done' && e.note_id
                        ? `note #${e.note_id}`
                        : e.reason ?? '—'}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-xs">
                      {e.llm_output_tokens ?? '—'}
                    </TableCell>
                    <TableCell className="text-right text-xs">{e.duration_ms ?? '—'}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {new Date(e.processed_at).toLocaleString()}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function KpiCard({
  label,
  value,
  tone,
  suffix,
  compact,
}: {
  label: string;
  value: number;
  tone?: 'success' | 'destructive' | 'muted';
  suffix?: string;
  compact?: boolean;
}): JSX.Element {
  const toneClass = tone
    ? {
        success: 'text-emerald-600',
        destructive: 'text-destructive',
        muted: 'text-muted-foreground',
      }[tone]
    : '';
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
        <div
          className={`${compact ? 'text-xl' : 'text-2xl'} mt-1 font-bold tabular-nums ${toneClass}`}
        >
          {value.toLocaleString()}
          {suffix && <span className="ml-1 text-base">{suffix}</span>}
        </div>
      </CardContent>
    </Card>
  );
}

function StatusBadge({ status }: { status: 'done' | 'skipped' | 'failed' }): JSX.Element {
  if (status === 'done') return <Badge variant="success">done</Badge>;
  if (status === 'failed') return <Badge variant="destructive">failed</Badge>;
  return <Badge variant="secondary">skipped</Badge>;
}
